import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ArrowDownWideNarrow,
  ArrowUpDown,
  ArrowUpNarrowWide,
  BarChart3,
  CalendarRange,
  ChevronLeft,
  ChevronRight,
  Clock3,
  Database,
  FileDown,
  FilterX,
  Power,
  RefreshCw,
  Search,
  Shield,
  SlidersHorizontal,
  Trash2,
  Users,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import {
  createRegisteredAdminUser,
  CreateRegisteredAdminUserPayload,
  deleteRegisteredUser,
  ListRegisteredUsersParams,
  listRegisteredUsers,
  RegisteredUserItem,
  RegisteredUserScope,
  RegisteredUserStatus,
  RegisteredUserSource,
  RegisteredUsersMeta,
  RegisteredUsersOrganization,
  RegisteredUsersSummary,
  RegisteredUsersTrendPoint,
  updateRegisteredUserStatus,
} from '../services/registeredUsersService';

const emptySummary: RegisteredUsersSummary = {
  total: 0,
  registered: 0,
  anonymous: 0,
  enabled: 0,
  disabled: 0,
  amministratori: 0,
  superadmin: 0,
  utenti: 0,
  merged: 0,
  fromUsers: 0,
  fromAuthUsers: 0,
};

const emptyMeta: RegisteredUsersMeta = {
  dataOrigin: 'modern_registry',
  total: 0,
  unfilteredTotal: 0,
  snapshotAt: null,
};

type DatePreset = 'all' | '7d' | '30d' | '90d' | 'custom';
type SortField = 'createdAt' | 'lastSeenAt' | 'username' | 'organization' | 'category' | 'source';
type SortDirection = 'asc' | 'desc';

const datePresetOptions: Array<{ value: DatePreset; label: string }> = [
  { value: 'all', label: 'Sempre' },
  { value: '7d', label: 'Ultimi 7 giorni' },
  { value: '30d', label: 'Ultimi 30 giorni' },
  { value: '90d', label: 'Ultimi 90 giorni' },
  { value: 'custom', label: 'Intervallo personalizzato' },
];

function formatDate(value?: string | null): string {
  if (!value) return 'n/d';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'n/d';
  return date.toLocaleString('it-IT');
}

function formatUserIdentitySubtitle(item: RegisteredUserItem): string {
  const name = [item.firstName, item.lastName].filter(Boolean).join(' ');
  if (name) return name;
  if (item.source === 'mobile_app' && !item.manageable && item.id) {
    return `Solo tracciamento app · ID legacy ${item.id}`;
  }
  return 'n/d';
}

function formatUserEmailCell(item: RegisteredUserItem): string {
  if (item.email) return item.email;
  if (item.source === 'mobile_app' && !item.manageable) return 'Non sincronizzato';
  return 'n/d';
}

function sourceMeta(source?: RegisteredUserSource) {
  if (source === 'legacy_access') return { label: 'Accesso legacy', className: 'bg-slate-100 text-slate-700' };
  if (source === 'combined') return { label: 'App + Creator', className: 'bg-violet-100 text-violet-700' };
  if (source === 'creator_activated') return { label: 'Creator attivato', className: 'bg-blue-100 text-blue-700' };
  if (source === 'creator_request') return { label: 'Richiesta creator', className: 'bg-amber-100 text-amber-700' };
  return { label: 'Utente app', className: 'bg-emerald-100 text-emerald-700' };
}

function registrationStatusMeta(status?: RegisteredUserItem['registrationStatus']) {
  if (status === 'legacy_access') return { label: 'Accesso legacy', className: 'bg-slate-100 text-slate-700' };
  if (status === 'pending') return { label: 'In attesa', className: 'bg-amber-100 text-amber-700' };
  if (status === 'approved') return { label: 'Approvato', className: 'bg-sky-100 text-sky-700' };
  if (status === 'activated') return { label: 'Attivato', className: 'bg-blue-100 text-blue-700' };
  if (status === 'rejected') return { label: 'Rifiutato', className: 'bg-red-100 text-red-700' };
  return { label: 'Utente app attivo', className: 'bg-emerald-100 text-emerald-700' };
}

function isoDay(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function resolveDateRange(preset: DatePreset, fromValue: string, toValue: string): { createdFrom?: string; createdTo?: string } {
  if (preset === 'all') return {};
  if (preset === 'custom') {
    return {
      createdFrom: fromValue || undefined,
      createdTo: toValue || undefined,
    };
  }
  const now = new Date();
  const dayOffset = preset === '7d' ? 7 : preset === '30d' ? 30 : 90;
  const from = new Date(now);
  from.setDate(now.getDate() - dayOffset);
  return {
    createdFrom: isoDay(from),
    createdTo: isoDay(now),
  };
}

function resolveSortButtonClass(active: boolean) {
  if (active) {
    return 'border-primary bg-primary text-white shadow-sm';
  }
  return 'border-border bg-white text-text-secondary hover:border-primary/30 hover:text-text-primary';
}

const RegisteredUsers = () => {
  const { isSuperAdmin } = useAuth();
  const canManage = isSuperAdmin;

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [scope, setScope] = useState<RegisteredUserScope>('all');
  const [searchInput, setSearchInput] = useState('');
  const [searchText, setSearchText] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<'all' | 'utente' | 'amministratore' | 'superadmin' | 'anonimo'>('all');
  const [statusFilter, setStatusFilter] = useState<RegisteredUserStatus>('all');
  const [sourceFilter, setSourceFilter] = useState<'all' | RegisteredUserSource>('all');
  const [organizationFilter, setOrganizationFilter] = useState<'all' | number>('all');
  const [sortBy, setSortBy] = useState<SortField>('createdAt');
  const [sortDir, setSortDir] = useState<SortDirection>('desc');
  const [datePreset, setDatePreset] = useState<DatePreset>('all');
  const [createdFrom, setCreatedFrom] = useState('');
  const [createdTo, setCreatedTo] = useState('');
  const [lastSeenPreset, setLastSeenPreset] = useState<DatePreset>('all');
  const [lastSeenFrom, setLastSeenFrom] = useState('');
  const [lastSeenTo, setLastSeenTo] = useState('');
  const [pageSize, setPageSize] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);

  const [users, setUsers] = useState<RegisteredUserItem[]>([]);
  const [summary, setSummary] = useState<RegisteredUsersSummary>(emptySummary);
  const [globalSummary, setGlobalSummary] = useState<RegisteredUsersSummary>(emptySummary);
  const [meta, setMeta] = useState<RegisteredUsersMeta>(emptyMeta);
  const [organizations, setOrganizations] = useState<RegisteredUsersOrganization[]>([]);
  const [trend, setTrend] = useState<RegisteredUsersTrendPoint[]>([]);
  const [reloadTick, setReloadTick] = useState(0);

  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [statusChangingId, setStatusChangingId] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [creatingAdmin, setCreatingAdmin] = useState(false);
  const [organizationOptions] = useState<Array<{ id: number; name: string }>>([]);
  const [createNewOrganization, setCreateNewOrganization] = useState(false);
  const [createForm, setCreateForm] = useState<CreateRegisteredAdminUserPayload>({
    username: '',
    email: '',
    firstName: '',
    lastName: '',
    temporaryPassword: '',
    organizationId: 0,
    organizationName: '',
    createOrganization: false,
    note: '',
  });

  const dateRange = useMemo(() => resolveDateRange(datePreset, createdFrom, createdTo), [datePreset, createdFrom, createdTo]);
  const lastSeenRange = useMemo(
    () => resolveDateRange(lastSeenPreset, lastSeenFrom, lastSeenTo),
    [lastSeenPreset, lastSeenFrom, lastSeenTo],
  );
  const queryParams = useMemo<ListRegisteredUsersParams>(() => ({
    scope,
    searchText: searchText.trim(),
    category: categoryFilter,
    status: statusFilter,
    source: sourceFilter,
    organizationId: organizationFilter === 'all' ? 'all' : organizationFilter,
    createdFrom: dateRange.createdFrom,
    createdTo: dateRange.createdTo,
    lastSeenFrom: lastSeenRange.createdFrom,
    lastSeenTo: lastSeenRange.createdTo,
    sortBy,
    sortDir,
  }), [
    scope,
    searchText,
    categoryFilter,
    statusFilter,
    sourceFilter,
    organizationFilter,
    dateRange.createdFrom,
    dateRange.createdTo,
    lastSeenRange.createdFrom,
    lastSeenRange.createdTo,
    sortBy,
    sortDir,
  ]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchText(searchInput.trim());
      setCurrentPage(1);
    }, 320);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const loadUsers = useCallback(async (opts?: { keepLoading?: boolean }) => {
    try {
      if (!opts?.keepLoading) setLoading(true);
      setError(null);
      const result = await listRegisteredUsers({
        ...queryParams,
        page: currentPage,
        pageSize,
      });
      setUsers(result.users || []);
      setSummary(result.summary || emptySummary);
      setGlobalSummary(result.globalSummary || result.summary || emptySummary);
      setMeta(result.meta || emptyMeta);
      setOrganizations(result.organizations || []);
      setTrend(result.trend || []);
      const totalPages = Number(result.meta?.totalPages || 0);
      if (totalPages > 0 && currentPage > totalPages) {
        setCurrentPage(totalPages);
      }
    } catch (err) {
      console.error(err);
      const detail = err instanceof Error ? err.message : '';
      setError(detail || 'Errore caricamento utenti iscritti.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [currentPage, pageSize, queryParams]);

  useEffect(() => {
    if (!canManage) return;
    void loadUsers();
  }, [canManage, loadUsers, reloadTick]);

  const filteredTotal = Number(meta.filteredTotal || summary.total || users.length || 0);
  const totalPages = Number(meta.totalPages || 1);
  const trendMax = useMemo(() => Math.max(...trend.map((item) => item.total), 1), [trend]);
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (scope !== 'all') count += 1;
    if (queryParams.searchText) count += 1;
    if (categoryFilter !== 'all') count += 1;
    if (statusFilter !== 'all') count += 1;
    if (sourceFilter !== 'all') count += 1;
    if (organizationFilter !== 'all') count += 1;
    if (datePreset !== 'all') count += 1;
    if (lastSeenPreset !== 'all') count += 1;
    if (sortBy !== 'createdAt' || sortDir !== 'desc') count += 1;
    return count;
  }, [
    scope,
    queryParams.searchText,
    categoryFilter,
    statusFilter,
    sourceFilter,
    organizationFilter,
    datePreset,
    lastSeenPreset,
    sortBy,
    sortDir,
  ]);

  const setDateSort = useCallback((field: 'createdAt' | 'lastSeenAt', direction: SortDirection) => {
    setSortBy(field);
    setSortDir(direction);
    setCurrentPage(1);
  }, []);

  const onCreateAdmin = async () => {
    const payload: CreateRegisteredAdminUserPayload = {
      username: String(createForm.username || '').trim(),
      email: String(createForm.email || '').trim().toLowerCase(),
      firstName: String(createForm.firstName || '').trim(),
      lastName: String(createForm.lastName || '').trim(),
      temporaryPassword: String(createForm.temporaryPassword || '').trim(),
      organizationId: Number(createForm.organizationId || 0),
      organizationName: String(createForm.organizationName || '').trim(),
      createOrganization: Boolean(createForm.createOrganization),
      note: String(createForm.note || '').trim(),
    };

    if (!payload.username || !payload.email || !payload.firstName || !payload.lastName || !payload.temporaryPassword) {
      setError('Compila tutti i campi obbligatori per creare l\'admin.');
      return;
    }
    if (payload.createOrganization) {
      if (!payload.organizationName) {
        setError('Inserisci il nome della nuova organizzazione.');
        return;
      }
      payload.organizationId = undefined;
    } else if (!Number.isFinite(Number(payload.organizationId || 0)) || Number(payload.organizationId || 0) <= 0) {
      setError('Seleziona un\'organizzazione valida.');
      return;
    }

    try {
      setCreatingAdmin(true);
      setError(null);
      await createRegisteredAdminUser(payload);
      setCreateModalOpen(false);
      setSuccess('Nuovo admin organizzazione creato correttamente.');
      setTimeout(() => setSuccess(null), 2500);
      await loadUsers({ keepLoading: true });
    } catch (err) {
      console.error(err);
      const detail = err instanceof Error ? err.message : '';
      setError(detail || 'Creazione admin non riuscita.');
    } finally {
      setCreatingAdmin(false);
    }
  };

  const onDelete = async (item: RegisteredUserItem) => {
    const label = item.username || item.email || `ID ${item.id}`;
    const confirmDelete = window.confirm(`Confermi la cancellazione definitiva dell'utente "${label}"?`);
    if (!confirmDelete) return;

    try {
      setDeletingId(item.id);
      setError(null);
      await deleteRegisteredUser(item.id);
      setSuccess('Utente cancellato correttamente.');
      setTimeout(() => setSuccess(null), 2500);
      await loadUsers({ keepLoading: true });
    } catch (err) {
      console.error(err);
      const detail = err instanceof Error ? err.message : '';
      setError(detail || 'Cancellazione utente non riuscita.');
    } finally {
      setDeletingId(null);
    }
  };

  const onToggleStatus = async (item: RegisteredUserItem, enabled: boolean) => {
    const label = item.username || item.email || `ID ${item.id}`;
    const actionLabel = enabled ? 'attivare' : 'disattivare';
    const confirmed = window.confirm(`Confermi di ${actionLabel} l'account "${label}"?`);
    if (!confirmed) return;

    try {
      setStatusChangingId(item.id);
      setError(null);
      await updateRegisteredUserStatus(item.id, enabled);
      setSuccess(enabled ? 'Utente attivato correttamente.' : 'Utente disattivato correttamente.');
      setTimeout(() => setSuccess(null), 2500);
      await loadUsers({ keepLoading: true });
    } catch (err) {
      console.error(err);
      const detail = err instanceof Error ? err.message : '';
      setError(detail || 'Aggiornamento stato utente non riuscito.');
    } finally {
      setStatusChangingId(null);
    }
  };

  const onExportCsv = async () => {
    try {
      setExporting(true);
      setError(null);
      const result = await listRegisteredUsers({
        ...queryParams,
        page: 1,
        pageSize: 10000,
      });

      const exportUsers = result.users || [];
      if (!exportUsers.length) {
        setError('Nessun utente disponibile per esportazione con i filtri correnti.');
        return;
      }

      const headers = [
        'id',
        'username',
        'email',
        'firstName',
        'lastName',
        'category',
        'source',
        'registrationStatus',
        'organization',
        'createdAt',
        'lastSeenAt',
        'platforms',
        'sessionCount',
        'eventCount',
        'enabled',
        'manageable',
      ];
      const esc = (v: unknown) => {
        const s = String(v ?? '');
        const safe = s.replace(/"/g, '""');
        return `"${safe}"`;
      };
      const csv = [
        headers.join(','),
        ...exportUsers.map((u) => [
          u.id,
          u.username,
          u.email || '',
          u.firstName || '',
          u.lastName || '',
          u.category || (u.anonymous ? 'anonimo' : 'utente'),
          u.source || '',
          u.registrationStatus || '',
          u.organizationName || '',
          u.createdAt || '',
          u.lastSeenAt || '',
          (u.platforms || []).join('|'),
          u.sessionCount ?? '',
          u.eventCount ?? '',
          u.enabled,
          u.manageable ?? false,
        ].map(esc).join(',')),
      ].join('\n');

      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `utenti_filtrati_${Date.now()}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      const detail = err instanceof Error ? err.message : '';
      setError(detail || 'Esportazione CSV non riuscita.');
    } finally {
      setExporting(false);
    }
  };

  if (!canManage) {
    return (
      <div className="card p-8 max-w-3xl">
        <h1 className="text-2xl font-bold text-text-primary flex items-center gap-2 mb-2">
          <Shield size={24} className="text-primary" />
          Utenti iscritti
        </h1>
        <p className="text-sm text-text-secondary">
          Questa sezione e riservata al superadmin.
        </p>
      </div>
    );
  }

  const rangeStart = filteredTotal === 0 ? 0 : ((currentPage - 1) * pageSize + 1);
  const rangeEnd = filteredTotal === 0 ? 0 : Math.min(filteredTotal, rangeStart + users.length - 1);
  const dateSortButtons: Array<{
    field: 'createdAt' | 'lastSeenAt';
    direction: SortDirection;
    label: string;
    icon: typeof ArrowUpNarrowWide;
  }> = [
    { field: 'createdAt', direction: 'asc', label: 'Iscrizione crescente', icon: ArrowUpNarrowWide },
    { field: 'createdAt', direction: 'desc', label: 'Iscrizione decrescente', icon: ArrowDownWideNarrow },
    { field: 'lastSeenAt', direction: 'asc', label: 'Ultima attivita crescente', icon: ArrowUpNarrowWide },
    { field: 'lastSeenAt', direction: 'desc', label: 'Ultima attivita decrescente', icon: ArrowDownWideNarrow },
  ];

  return (
    <div className="w-full max-w-none space-y-8 pb-8">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-text-primary flex items-center gap-3">
            <Users size={30} className="text-primary" />
            Registro utenti nuova piattaforma
          </h1>
          <p className="text-text-secondary mt-1">
            Vista unica di richieste creator e prime attivita reali in app, con mirror legacy usato solo per arricchire nome, email e organizzazione.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            className="btn-outline inline-flex items-center gap-2"
            onClick={() => {
              setRefreshing(true);
              setReloadTick((prev) => prev + 1);
            }}
            disabled={refreshing || loading}
          >
            <RefreshCw size={15} className={(refreshing || loading) ? 'animate-spin' : ''} />
            Aggiorna
          </button>
        </div>
      </div>

      {error && <div className="rounded-lg border border-error/30 bg-error/10 p-4 text-error">{error}</div>}
      {success && <div className="rounded-lg border border-green-200 bg-green-50 p-4 text-green-700">{success}</div>}

      <div className="card overflow-hidden border border-primary/15 bg-gradient-to-r from-primary/5 via-white to-sky-50">
        <div className="grid gap-4 px-5 py-5 xl:grid-cols-[minmax(0,1.5fr)_minmax(320px,0.9fr)]">
          <div>
            <p className="text-sm font-semibold text-text-primary flex items-center gap-2">
              <Database size={15} className="text-primary" />
              Fonte dati effettiva
            </p>
            <p className="mt-2 text-sm leading-6 text-text-secondary">
              La pagina unisce `mobileAnalyticsEvents` e `creatorRegistrationRequests`; `glooci_genia/users/authUsers`
              resta solo un supporto anagrafico per identita, organizzazione e operativita degli account.
            </p>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="rounded-2xl border border-white/70 bg-white/80 p-4 shadow-sm">
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-text-secondary">Origine</p>
              <p className="mt-2 text-sm font-semibold uppercase text-text-primary">{meta.dataOrigin || 'firestore'}</p>
            </div>
            <div className="rounded-2xl border border-white/70 bg-white/80 p-4 shadow-sm">
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-text-secondary">Snapshot</p>
              <p className="mt-2 text-sm font-semibold text-text-primary">{formatDate(meta.snapshotAt)}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-3 2xl:grid-cols-9">
        {[
          { label: 'Totale', value: globalSummary.total, tone: 'text-blue-600 bg-blue-50' },
          { label: 'Utenti app', value: globalSummary.appUsers || 0, tone: 'text-emerald-600 bg-emerald-50' },
          { label: 'Lead creator', value: globalSummary.creatorLeads || 0, tone: 'text-amber-600 bg-amber-50' },
          { label: 'Creator attivati', value: globalSummary.creatorActivated || 0, tone: 'text-blue-600 bg-blue-50' },
          { label: 'Pending', value: globalSummary.creatorPending || 0, tone: 'text-yellow-700 bg-yellow-50' },
          { label: 'Rifiutati', value: globalSummary.creatorRejected || 0, tone: 'text-red-600 bg-red-50' },
          { label: 'App + Creator', value: globalSummary.combined || 0, tone: 'text-violet-600 bg-violet-50' },
          { label: 'Attivi 30g', value: globalSummary.activeLast30d || 0, tone: 'text-cyan-600 bg-cyan-50' },
          { label: 'Accesso legacy', value: globalSummary.legacyAccess || 0, tone: 'text-slate-700 bg-slate-100' },
        ].map((item) => (
          <div key={item.label} className="card p-3">
            <div className={`w-7 h-7 rounded-lg ${item.tone} flex items-center justify-center mb-2`}>
              <Users size={14} />
            </div>
            <p className="text-xl font-bold text-text-primary leading-none">{loading ? '–' : item.value}</p>
            <p className="text-xs text-text-secondary mt-1">{item.label}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-6 2xl:grid-cols-[minmax(0,1.75fr)_420px]">
        <div className="card space-y-5 p-5">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <p className="text-sm font-semibold text-text-primary flex items-center gap-2">
                <SlidersHorizontal size={15} className="text-primary" />
                Filtri e ordinamenti
              </p>
              <p className="mt-1 text-sm text-text-secondary">
                Cerca per segmento, stato, organizzazione e periodo. Gli ordinamenti rapidi sulle date sono separati per non creare ambiguita.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {activeFilterCount > 0 && (
                <span className="rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
                  {activeFilterCount} filtri attivi
                </span>
              )}
              <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-text-secondary">
                {loading ? 'Carico...' : `${filteredTotal} risultati`}
              </span>
            </div>
          </div>

          <div className="grid gap-3 xl:grid-cols-[minmax(0,1.35fr)_minmax(280px,0.65fr)]">
            <label className="text-sm">
              <span className="mb-1 block text-text-secondary">Ricerca utente</span>
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary" />
                <input
                  className="w-full rounded-2xl border border-border bg-white pl-10 pr-4 py-3"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="username, email, nome, cognome, organizzazione..."
                />
              </div>
            </label>

            <label className="text-sm">
              <span className="mb-1 block text-text-secondary">Organizzazione</span>
              <select
                className="w-full rounded-2xl border border-border bg-white px-4 py-3"
                value={organizationFilter}
                onChange={(e) => {
                  const value = e.target.value;
                  setOrganizationFilter(value === 'all' ? 'all' : Number(value));
                  setCurrentPage(1);
                }}
              >
                <option value="all">Tutte</option>
                {organizations.map((org) => (
                  <option key={org.id} value={org.id}>{org.name}</option>
                ))}
              </select>
            </label>
          </div>

          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
            <label className="text-sm">
              <span className="mb-1 block text-text-secondary">Segmento</span>
              <select
                className="w-full rounded-2xl border border-border bg-white px-4 py-3"
                value={scope}
                onChange={(e) => {
                  setScope(e.target.value as typeof scope);
                  setCurrentPage(1);
                }}
              >
                <option value="all">Tutti</option>
                <option value="app">Solo utenti app</option>
                <option value="creator">Solo creator</option>
                <option value="legacy">Solo accessi legacy</option>
              </select>
            </label>

            <label className="text-sm">
              <span className="mb-1 block text-text-secondary">Fonte dati</span>
              <select
                className="w-full rounded-2xl border border-border bg-white px-4 py-3"
                value={sourceFilter}
                onChange={(e) => {
                  setSourceFilter(e.target.value as typeof sourceFilter);
                  setCurrentPage(1);
                }}
              >
                <option value="all">Tutte le fonti</option>
                <option value="combined">App + creator</option>
                <option value="mobile_app">Solo utenti app</option>
                <option value="creator_request">Solo richieste creator</option>
                <option value="creator_activated">Solo creator attivati</option>
                <option value="legacy_access">Solo accesso legacy</option>
              </select>
            </label>

            <label className="text-sm">
              <span className="mb-1 block text-text-secondary">Categoria</span>
              <select
                className="w-full rounded-2xl border border-border bg-white px-4 py-3"
                value={categoryFilter}
                onChange={(e) => {
                  setCategoryFilter(e.target.value as typeof categoryFilter);
                  setCurrentPage(1);
                }}
              >
                <option value="all">Tutte</option>
                <option value="utente">Utenti</option>
                <option value="amministratore">Amministratori</option>
                <option value="superadmin">Superadmin</option>
                <option value="anonimo">Anonimi</option>
              </select>
            </label>

            <label className="text-sm">
              <span className="mb-1 block text-text-secondary">Stato registrazione</span>
              <select
                className="w-full rounded-2xl border border-border bg-white px-4 py-3"
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value as typeof statusFilter);
                  setCurrentPage(1);
                }}
              >
                <option value="all">Tutti</option>
                <option value="active_app_user">Utenti app attivi</option>
                <option value="pending">Creator in attesa</option>
                <option value="approved">Creator approvati</option>
                <option value="activated">Creator attivati</option>
                <option value="rejected">Creator rifiutati</option>
                <option value="legacy_access">Accesso legacy</option>
              </select>
            </label>
          </div>

          <div className="grid gap-4 xl:grid-cols-2">
            <div className="rounded-3xl border border-border bg-slate-50/80 p-4">
              <div className="mb-3 flex items-center gap-2">
                <CalendarRange size={16} className="text-primary" />
                <div>
                  <p className="text-sm font-semibold text-text-primary">Filtro per iscrizione</p>
                  <p className="text-xs text-text-secondary">Usa la data di ingresso reale nel registro.</p>
                </div>
              </div>
              <div className="grid gap-3 md:grid-cols-3">
                <label className="text-sm md:col-span-3">
                  <span className="mb-1 block text-text-secondary">Periodo</span>
                  <select
                    className="w-full rounded-2xl border border-border bg-white px-4 py-3"
                    value={datePreset}
                    onChange={(e) => {
                      setDatePreset(e.target.value as DatePreset);
                      setCurrentPage(1);
                    }}
                  >
                    {datePresetOptions.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </label>
                <label className="text-sm">
                  <span className="mb-1 block text-text-secondary">Da</span>
                  <input
                    type="date"
                    className="w-full rounded-2xl border border-border bg-white px-4 py-3 disabled:bg-slate-100"
                    value={createdFrom}
                    onChange={(e) => {
                      setCreatedFrom(e.target.value);
                      setCurrentPage(1);
                    }}
                    disabled={datePreset !== 'custom'}
                  />
                </label>
                <label className="text-sm">
                  <span className="mb-1 block text-text-secondary">A</span>
                  <input
                    type="date"
                    className="w-full rounded-2xl border border-border bg-white px-4 py-3 disabled:bg-slate-100"
                    value={createdTo}
                    onChange={(e) => {
                      setCreatedTo(e.target.value);
                      setCurrentPage(1);
                    }}
                    disabled={datePreset !== 'custom'}
                  />
                </label>
                <div className="flex items-end">
                  <button
                    type="button"
                    className={`w-full rounded-2xl border px-4 py-3 text-sm font-medium transition ${resolveSortButtonClass(sortBy === 'createdAt' && sortDir === 'asc')}`}
                    onClick={() => setDateSort('createdAt', 'asc')}
                  >
                    Iscrizione crescente
                  </button>
                </div>
              </div>
            </div>

            <div className="rounded-3xl border border-border bg-slate-50/80 p-4">
              <div className="mb-3 flex items-center gap-2">
                <Clock3 size={16} className="text-primary" />
                <div>
                  <p className="text-sm font-semibold text-text-primary">Filtro per ultima attivita</p>
                  <p className="text-xs text-text-secondary">Restringi in base all’ultima presenza rilevata.</p>
                </div>
              </div>
              <div className="grid gap-3 md:grid-cols-3">
                <label className="text-sm md:col-span-3">
                  <span className="mb-1 block text-text-secondary">Periodo</span>
                  <select
                    className="w-full rounded-2xl border border-border bg-white px-4 py-3"
                    value={lastSeenPreset}
                    onChange={(e) => {
                      setLastSeenPreset(e.target.value as DatePreset);
                      setCurrentPage(1);
                    }}
                  >
                    {datePresetOptions.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </label>
                <label className="text-sm">
                  <span className="mb-1 block text-text-secondary">Da</span>
                  <input
                    type="date"
                    className="w-full rounded-2xl border border-border bg-white px-4 py-3 disabled:bg-slate-100"
                    value={lastSeenFrom}
                    onChange={(e) => {
                      setLastSeenFrom(e.target.value);
                      setCurrentPage(1);
                    }}
                    disabled={lastSeenPreset !== 'custom'}
                  />
                </label>
                <label className="text-sm">
                  <span className="mb-1 block text-text-secondary">A</span>
                  <input
                    type="date"
                    className="w-full rounded-2xl border border-border bg-white px-4 py-3 disabled:bg-slate-100"
                    value={lastSeenTo}
                    onChange={(e) => {
                      setLastSeenTo(e.target.value);
                      setCurrentPage(1);
                    }}
                    disabled={lastSeenPreset !== 'custom'}
                  />
                </label>
                <div className="flex items-end">
                  <button
                    type="button"
                    className={`w-full rounded-2xl border px-4 py-3 text-sm font-medium transition ${resolveSortButtonClass(sortBy === 'lastSeenAt' && sortDir === 'asc')}`}
                    onClick={() => setDateSort('lastSeenAt', 'asc')}
                  >
                    Ultima attivita crescente
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="rounded-3xl border border-border bg-white p-4">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <p className="text-sm font-semibold text-text-primary flex items-center gap-2">
                  <ArrowUpDown size={15} className="text-primary" />
                  Ordinamenti rapidi
                </p>
                <p className="mt-1 text-xs text-text-secondary">
                  Controlli separati per `Iscrizione` e `Ultima attivita`, piu un ordinamento secondario per nome, organizzazione o stato.
                </p>
              </div>
              <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
                {dateSortButtons.map((button) => {
                  const Icon = button.icon;
                  const active = sortBy === button.field && sortDir === button.direction;
                  return (
                    <button
                      key={`${button.field}-${button.direction}`}
                      type="button"
                      className={`inline-flex items-center justify-center gap-2 rounded-2xl border px-4 py-3 text-sm font-medium transition ${resolveSortButtonClass(active)}`}
                      onClick={() => setDateSort(button.field, button.direction)}
                    >
                      <Icon size={15} />
                      {button.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="mt-4 grid gap-3 md:grid-cols-[minmax(0,1fr)_180px]">
              <label className="text-sm">
                <span className="mb-1 block text-text-secondary">Ordinamento secondario</span>
                <select
                  className="w-full rounded-2xl border border-border bg-white px-4 py-3"
                  value={sortBy}
                  onChange={(e) => {
                    setSortBy(e.target.value as SortField);
                    setCurrentPage(1);
                  }}
                >
                  <option value="createdAt">Iscrizione</option>
                  <option value="lastSeenAt">Ultima attività</option>
                  <option value="username">Nome utente</option>
                  <option value="organization">Organizzazione</option>
                  <option value="category">Categoria</option>
                  <option value="source">Fonte</option>
                </select>
              </label>

              <label className="text-sm">
                <span className="mb-1 block text-text-secondary">Direzione</span>
                <select
                  className="w-full rounded-2xl border border-border bg-white px-4 py-3"
                  value={sortDir}
                  onChange={(e) => {
                    setSortDir(e.target.value as SortDirection);
                    setCurrentPage(1);
                  }}
                >
                  <option value="desc">Decrescente</option>
                  <option value="asc">Crescente</option>
                </select>
              </label>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              className="btn-outline"
              onClick={() => {
                if (currentPage === 1) {
                  setReloadTick((prev) => prev + 1);
                } else {
                  setCurrentPage(1);
                }
              }}
            >
              Aggiorna risultati
            </button>
            <button
              type="button"
              className="btn-outline inline-flex items-center gap-2"
              onClick={() => {
                setScope('all');
                setSearchInput('');
                setSearchText('');
                setCategoryFilter('all');
                setStatusFilter('all');
                setSourceFilter('all');
                setOrganizationFilter('all');
                setDatePreset('all');
                setCreatedFrom('');
                setCreatedTo('');
                setLastSeenPreset('all');
                setLastSeenFrom('');
                setLastSeenTo('');
                setSortBy('createdAt');
                setSortDir('desc');
                if (currentPage === 1) {
                  setReloadTick((prev) => prev + 1);
                } else {
                  setCurrentPage(1);
                }
              }}
            >
              <FilterX size={14} />
              Reset filtri
            </button>
            <p className="text-xs text-text-secondary ml-auto">
              Filtrati: <span className="font-semibold text-text-primary">{filteredTotal}</span> su{' '}
              <span className="font-semibold text-text-primary">{globalSummary.total}</span> utenti
            </p>
          </div>

          <div className="rounded-xl border border-border p-4">
            <p className="text-sm font-semibold text-text-primary mb-3 flex items-center gap-2">
              <BarChart3 size={15} className="text-primary" />
              Nuove persone rilevate (ultimi 14 giorni)
            </p>
            <div className="grid gap-2 items-end h-40" style={{ gridTemplateColumns: `repeat(${trend.length || 1}, minmax(0, 1fr))` }}>
              {trend.map((point) => {
                const height = Math.max(6, Math.round((point.total / trendMax) * 140));
                return (
                  <div key={point.label} className="flex flex-col items-center justify-end gap-1">
                    <div title={`${point.label}: ${point.total}`} className="w-full max-w-6 rounded bg-primary/80" style={{ height }} />
                    <span className="text-[10px] text-text-secondary">{point.label}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="card space-y-4 p-5">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-text-secondary">Stato gestione</h2>
          <p className="text-sm text-text-secondary">
            Gli utenti app entrano in elenco alla prima attività tracciata. Le richieste creator entrano alla creazione della richiesta.
          </p>
          <p className="text-sm text-text-secondary">
            Le azioni operative restano disponibili solo quando esiste un account mirror effettivamente gestibile.
          </p>
          <div className="rounded-2xl border border-border bg-slate-50 p-4 text-xs text-text-secondary space-y-1">
            <p>Ruoli anagrafici risolti:</p>
            <p><span className="font-semibold text-text-primary">Utenti:</span> {summary.utenti || 0}</p>
            <p><span className="font-semibold text-text-primary">Amministratori:</span> {summary.amministratori || 0}</p>
            <p><span className="font-semibold text-text-primary">Superadmin:</span> {summary.superadmin || 0}</p>
          </div>
          <div className="rounded-2xl border border-border bg-slate-50 p-4 text-xs text-text-secondary space-y-1">
            <p><span className="font-semibold text-text-primary">Origine dati:</span> {meta.dataOrigin || 'firestore'}</p>
            <p><span className="font-semibold text-text-primary">Snapshot:</span> {formatDate(meta.snapshotAt)}</p>
            <p><span className="font-semibold text-text-primary">Totale non filtrato:</span> {meta.unfilteredTotal ?? globalSummary.total}</p>
          </div>
        </div>
      </div>

      <div className="card overflow-hidden border border-border/80 shadow-sm">
        <div className="border-b border-border bg-white px-5 py-4">
          <div className="flex flex-col gap-2 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <h2 className="font-semibold text-text-primary">Persone registrate o rilevate</h2>
              <p className="mt-1 text-xs text-text-secondary">
                Ogni riga rappresenta una persona della piattaforma, con sorgente, stato onboarding, iscrizione e ultima attivita.
              </p>
            </div>
            <div className="text-xs text-text-secondary">
              Ordinamento attivo:{' '}
              <span className="font-semibold text-text-primary">
                {sortBy === 'createdAt'
                  ? `Iscrizione ${sortDir === 'asc' ? 'crescente' : 'decrescente'}`
                  : sortBy === 'lastSeenAt'
                    ? `Ultima attivita ${sortDir === 'asc' ? 'crescente' : 'decrescente'}`
                    : `${sortBy} ${sortDir === 'asc' ? 'crescente' : 'decrescente'}`}
              </span>
            </div>
          </div>
        </div>
        <div className="border-b border-border bg-slate-50/70 px-5 py-3 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <p className="text-xs text-text-secondary">
            Risultati: <span className="text-text-primary font-medium">{loading ? '–' : filteredTotal}</span>
            {!loading && filteredTotal > 0 && (
              <>
                {' '}| Visualizzati {rangeStart}-{rangeEnd}
              </>
            )}
          </p>
          <div className="flex items-center gap-2">
            <label className="text-xs text-text-secondary">
              Righe
              <select
                className="ml-2 rounded-lg border border-border px-2 py-1 text-xs"
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setCurrentPage(1);
                }}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </label>
            <button
              type="button"
              className="btn-outline inline-flex items-center gap-2"
              disabled={loading || filteredTotal === 0 || exporting}
              onClick={() => void onExportCsv()}
            >
              <FileDown size={16} />
              {exporting ? 'Esportazione...' : 'Esporta CSV'}
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[1460px] text-sm">
            <thead className="sticky top-0 z-10 bg-white/95 text-text-secondary text-xs uppercase tracking-wider backdrop-blur">
              <tr>
                <th className="text-left px-5 py-3">Utente</th>
                <th className="text-left px-5 py-3">Email</th>
                <th className="text-left px-5 py-3">Fonte</th>
                <th className="text-left px-5 py-3">Stato</th>
                <th className="text-left px-5 py-3">Organizzazione</th>
                <th className="text-left px-5 py-3">Iscrizione</th>
                <th className="text-left px-5 py-3">Ultima attività</th>
                <th className="sticky right-0 text-right px-5 py-3 bg-white/95 backdrop-blur">Azioni</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {loading ? (
                Array.from({ length: 8 }).map((_, idx) => (
                  <tr key={idx}>
                    <td className="px-5 py-4"><div className="h-4 rounded bg-gray-100 animate-pulse" /></td>
                    <td className="px-5 py-4"><div className="h-4 rounded bg-gray-100 animate-pulse" /></td>
                    <td className="px-5 py-4"><div className="h-4 rounded bg-gray-100 animate-pulse" /></td>
                    <td className="px-5 py-4"><div className="h-4 rounded bg-gray-100 animate-pulse" /></td>
                    <td className="px-5 py-4"><div className="h-4 rounded bg-gray-100 animate-pulse" /></td>
                    <td className="px-5 py-4"><div className="h-4 rounded bg-gray-100 animate-pulse" /></td>
                    <td className="px-5 py-4"><div className="h-4 rounded bg-gray-100 animate-pulse" /></td>
                    <td className="sticky right-0 px-5 py-4 bg-white"><div className="h-4 rounded bg-gray-100 animate-pulse" /></td>
                  </tr>
                ))
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-5 py-10 text-center text-text-secondary">
                    Nessun utente per i filtri selezionati.
                  </td>
                </tr>
              ) : (
                users.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50/80">
                    <td className="px-5 py-4 align-top">
                      <p className="font-medium text-text-primary">{item.username || `utente-${item.id}`}</p>
                      <div className="flex flex-wrap items-center gap-2 mt-1">
                        <p className="text-xs text-text-secondary">{formatUserIdentitySubtitle(item)}</p>
                        <span
                          className={`text-[11px] px-2 py-0.5 rounded-full ${
                            item.category === 'superadmin'
                              ? 'bg-violet-100 text-violet-700'
                              : item.category === 'amministratore'
                                ? 'bg-blue-100 text-blue-700'
                                : 'bg-green-100 text-green-700'
                          }`}
                        >
                          {item.category === 'superadmin'
                            ? 'Superadmin'
                            : item.category === 'amministratore'
                              ? 'Amministratore'
                              : 'Utente'}
                        </span>
                      </div>
                    </td>
                    <td className="px-5 py-4 align-top text-text-secondary">{formatUserEmailCell(item)}</td>
                    <td className="px-5 py-4 align-top">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${sourceMeta(item.source).className}`}>
                        {sourceMeta(item.source).label}
                      </span>
                      {(item.platforms || []).length > 0 && (
                        <p className="text-[11px] text-text-secondary mt-1">
                          {(item.platforms || []).join(', ')} · {item.sessionCount || 0} sessioni · {item.eventCount || 0} eventi
                        </p>
                      )}
                    </td>
                    <td className="px-5 py-4 align-top">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${registrationStatusMeta(item.registrationStatus).className}`}>
                        {registrationStatusMeta(item.registrationStatus).label}
                      </span>
                    </td>
                    <td className="px-5 py-4 align-top text-text-secondary">{item.organizationName || 'n/d'}</td>
                    <td className="px-5 py-4 align-top text-xs text-text-secondary">{formatDate(item.createdAt)}</td>
                    <td className="px-5 py-4 align-top text-xs text-text-secondary">{formatDate(item.lastSeenAt)}</td>
                    <td className="sticky right-0 bg-white px-5 py-4 align-top">
                      {!item.manageable ? (
                        <div className="text-right text-xs text-text-secondary">
                          {item.source === 'mobile_app' ? 'Solo lettura' : 'n/d'}
                        </div>
                      ) : (
                        <div className="flex justify-end gap-2">
                          <button
                            type="button"
                            className="btn-outline text-xs inline-flex items-center gap-1"
                            disabled={Boolean(item.guarded) || statusChangingId === item.id}
                            onClick={() => void onToggleStatus(item, !item.enabled)}
                            title={item.guarded ? 'Account protetto' : undefined}
                          >
                            <Power size={12} />
                            {statusChangingId === item.id
                              ? 'Aggiorno...'
                              : item.guarded
                                ? 'Protetto'
                                : item.enabled
                                  ? 'Disattiva'
                                  : 'Attiva'}
                          </button>
                          <button
                            type="button"
                            className="btn-outline text-xs inline-flex items-center gap-1"
                            disabled={Boolean(item.guarded) || deletingId === item.id}
                            onClick={() => void onDelete(item)}
                            title={item.guarded ? 'Account protetto' : undefined}
                          >
                            <Trash2 size={12} />
                            {deletingId === item.id ? 'Elimino...' : 'Elimina'}
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {!loading && filteredTotal > 0 && (
          <div className="px-5 py-3 border-t border-border flex items-center justify-between">
            <p className="text-xs text-text-secondary">
              Pagina <span className="font-semibold text-text-primary">{currentPage}</span> di{' '}
              <span className="font-semibold text-text-primary">{totalPages}</span>
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                className="btn-outline p-2"
                disabled={currentPage <= 1}
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
              >
                <ChevronLeft size={14} />
              </button>
              <button
                type="button"
                className="btn-outline p-2"
                disabled={currentPage >= totalPages}
                onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
              >
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        )}
      </div>

      {createModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-2xl rounded-2xl bg-white shadow-2xl border border-border">
            <div className="flex items-center justify-between border-b border-border px-6 py-4">
              <h3 className="text-lg font-semibold text-text-primary">Crea nuovo admin organizzazione</h3>
              <button
                type="button"
                className="btn-outline text-xs"
                onClick={() => setCreateModalOpen(false)}
                disabled={creatingAdmin}
              >
                Chiudi
              </button>
            </div>
            <div className="space-y-4 px-6 py-5">
              <div className="grid gap-3 md:grid-cols-2">
                <label className="text-sm">
                  <span className="mb-1 block text-text-secondary">Username *</span>
                  <input
                    className="w-full rounded-xl border border-border bg-white px-3 py-2"
                    value={createForm.username}
                    onChange={(e) => setCreateForm((prev) => ({ ...prev, username: e.target.value }))}
                    placeholder="es. mario.rossi"
                    disabled={creatingAdmin}
                  />
                </label>
                <label className="text-sm">
                  <span className="mb-1 block text-text-secondary">Email *</span>
                  <input
                    type="email"
                    className="w-full rounded-xl border border-border bg-white px-3 py-2"
                    value={createForm.email}
                    onChange={(e) => setCreateForm((prev) => ({ ...prev, email: e.target.value }))}
                    placeholder="nome@dominio.it"
                    disabled={creatingAdmin}
                  />
                </label>
              </div>
              <div className="grid gap-3 md:grid-cols-2">
                <label className="text-sm">
                  <span className="mb-1 block text-text-secondary">Nome *</span>
                  <input
                    className="w-full rounded-xl border border-border bg-white px-3 py-2"
                    value={createForm.firstName}
                    onChange={(e) => setCreateForm((prev) => ({ ...prev, firstName: e.target.value }))}
                    placeholder="Nome"
                    disabled={creatingAdmin}
                  />
                </label>
                <label className="text-sm">
                  <span className="mb-1 block text-text-secondary">Cognome *</span>
                  <input
                    className="w-full rounded-xl border border-border bg-white px-3 py-2"
                    value={createForm.lastName}
                    onChange={(e) => setCreateForm((prev) => ({ ...prev, lastName: e.target.value }))}
                    placeholder="Cognome"
                    disabled={creatingAdmin}
                  />
                </label>
              </div>
              <div className="grid gap-3 md:grid-cols-2">
                <label className="text-sm">
                  <span className="mb-1 block text-text-secondary">Password temporanea *</span>
                  <input
                    type="password"
                    className="w-full rounded-xl border border-border bg-white px-3 py-2"
                    value={createForm.temporaryPassword}
                    onChange={(e) => setCreateForm((prev) => ({ ...prev, temporaryPassword: e.target.value }))}
                    placeholder="minimo 8 caratteri"
                    disabled={creatingAdmin}
                  />
                </label>
                <label className="text-sm">
                  <span className="mb-1 block text-text-secondary">Organizzazione *</span>
                  {!createNewOrganization ? (
                    <select
                      className="w-full rounded-xl border border-border bg-white px-3 py-2"
                      value={createForm.organizationId || 0}
                      onChange={(e) => {
                        const selectedId = Number(e.target.value || 0);
                        const selectedOrganization = organizationOptions.find((org) => org.id === selectedId);
                        setCreateForm((prev) => ({
                          ...prev,
                          organizationId: selectedId,
                          organizationName: selectedOrganization?.name || '',
                          createOrganization: false,
                        }));
                      }}
                      disabled={creatingAdmin}
                    >
                      <option value={0}>Seleziona organizzazione</option>
                      {organizationOptions.map((org) => (
                        <option key={org.id} value={org.id}>{org.name}</option>
                      ))}
                    </select>
                  ) : (
                    <input
                      className="w-full rounded-xl border border-border bg-white px-3 py-2"
                      value={createForm.organizationName || ''}
                      onChange={(e) => setCreateForm((prev) => ({
                        ...prev,
                        organizationName: e.target.value,
                        organizationId: undefined,
                        createOrganization: true,
                      }))}
                      placeholder="Nome nuova organizzazione"
                      disabled={creatingAdmin}
                    />
                  )}
                  <button
                    type="button"
                    className="mt-2 text-xs text-primary hover:underline"
                    disabled={creatingAdmin}
                    onClick={() => {
                      setCreateNewOrganization((prev) => {
                        const next = !prev;
                        if (next) {
                          setCreateForm((current) => ({
                            ...current,
                            organizationId: undefined,
                            organizationName: '',
                            createOrganization: true,
                          }));
                        } else {
                          const fallbackId = Number(organizationOptions?.[0]?.id || 0);
                          const fallbackName = String(organizationOptions?.[0]?.name || '');
                          setCreateForm((current) => ({
                            ...current,
                            organizationId: fallbackId > 0 ? fallbackId : 0,
                            organizationName: fallbackName,
                            createOrganization: false,
                          }));
                        }
                        return next;
                      });
                    }}
                  >
                    {createNewOrganization
                      ? 'Usa organizzazione esistente'
                      : 'Organizzazione non in lista? Creane una nuova'}
                  </button>
                </label>
              </div>
              <label className="text-sm">
                <span className="mb-1 block text-text-secondary">Nota interna (opzionale)</span>
                <textarea
                  className="w-full rounded-xl border border-border bg-white px-3 py-2"
                  rows={3}
                  value={createForm.note || ''}
                  onChange={(e) => setCreateForm((prev) => ({ ...prev, note: e.target.value }))}
                  placeholder="Nota amministrativa"
                  disabled={creatingAdmin}
                />
              </label>
            </div>
            <div className="flex items-center justify-end gap-2 border-t border-border px-6 py-4">
              <button
                type="button"
                className="btn-outline"
                onClick={() => setCreateModalOpen(false)}
                disabled={creatingAdmin}
              >
                Annulla
              </button>
              <button
                type="button"
                className="btn-primary inline-flex items-center gap-2"
                onClick={() => void onCreateAdmin()}
                disabled={creatingAdmin}
              >
                {creatingAdmin ? 'Creazione...' : 'Crea admin'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RegisteredUsers;
